import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { CheckCircle, FileText } from '@phosphor-icons/react';
import { formatCurrency } from '../../lib/utils';

const EmptyClaims = () => {
  const navigate = useNavigate();
  return (
    <div className="text-center py-8 text-slate-500">
      <FileText size={40} className="mx-auto mb-3 text-slate-300" />
      <p>No pending claims</p>
      <Button
        variant="link"
        className="text-indigo-600 mt-2"
        onClick={() => navigate('/claims/new')}
      >
        Create a new claim
      </Button>
    </div>
  );
};

const ClaimRow = ({ claim, isSelected, onToggle }) => (
  <TableRow>
    <TableCell>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => onToggle(claim.id, e.target.checked)}
        className="rounded"
      />
    </TableCell>
    <TableCell>
      <span className="font-mono text-xs">{claim.claim_number}</span>
    </TableCell>
    <TableCell className="font-medium">{claim.patient_name}</TableCell>
    <TableCell>
      <span className="font-mono text-indigo-700">{claim.service_code}</span>
      {claim.modifier_1 && <span className="text-slate-500">-{claim.modifier_1}</span>}
    </TableCell>
    <TableCell>
      <Badge variant="outline">{claim.payer_type || 'ODM'}</Badge>
    </TableCell>
    <TableCell>
      <span className="font-mono">{formatCurrency(claim.estimated_reimbursement)}</span>
    </TableCell>
  </TableRow>
);

const PendingClaimsTab = ({
  claims,
  selectedClaims,
  onToggleAll,
  onToggle,
  onMarkSubmitted,
}) => (
  <Card className="border-slate-200">
    <CardHeader className="flex flex-row items-center justify-between">
      <div>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText size={20} className="text-amber-600" />
          Pending Claims for Submission
        </CardTitle>
        <CardDescription>{claims.length} claims ready for ODM submission</CardDescription>
      </div>
      {selectedClaims.length > 0 && (
        <Button
          variant="outline"
          onClick={onMarkSubmitted}
          data-testid="mark-submitted-btn"
        >
          <CheckCircle size={16} className="mr-2" />
          Mark {selectedClaims.length} Submitted
        </Button>
      )}
    </CardHeader>
    <CardContent className="p-0">
      {claims.length === 0 ? (
        <EmptyClaims />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  checked={selectedClaims.length === claims.length}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="rounded"
                />
              </TableHead>
              <TableHead>Claim #</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Payer</TableHead>
              <TableHead>Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claims.map((claim) => (
              <ClaimRow
                key={claim.id}
                claim={claim}
                isSelected={selectedClaims.includes(claim.id)}
                onToggle={onToggle}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default PendingClaimsTab;
